const MIN_STEP = 4;
const REVERSAL_RADIANS = (120 * Math.PI) / 180;

function turningOf(steps) {
  let turning = 0;
  let net = 0;
  let reversals = 0;
  for (let index = 1; index < steps.length; index += 1) {
    const previous = Math.atan2(steps[index - 1].dy, steps[index - 1].dx);
    const current = Math.atan2(steps[index].dy, steps[index].dx);
    let delta = current - previous;
    while (delta > Math.PI) delta -= Math.PI * 2;
    while (delta < -Math.PI) delta += Math.PI * 2;
    turning += Math.abs(delta);
    net += delta;
    if (Math.abs(delta) > REVERSAL_RADIANS) reversals += 1;
  }
  return {
    turningDeg: (turning * 180) / Math.PI,
    netDeg: (net * 180) / Math.PI,
    reversals,
  };
}

function orientation(a, b, c) {
  return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
}

function segmentsCross(a, b, c, d) {
  const o1 = orientation(a, b, c);
  const o2 = orientation(a, b, d);
  const o3 = orientation(c, d, a);
  const o4 = orientation(c, d, b);
  if (o1 === 0 || o2 === 0 || o3 === 0 || o4 === 0) return false;
  return (o1 > 0) !== (o2 > 0) && (o3 > 0) !== (o4 > 0);
}

function countCrossings(samples) {
  const points = [];
  for (let index = 1; index < samples.length; index += 1) {
    const from = samples[index - 1];
    const to = samples[index];
    if (Math.hypot(to.x - from.x, to.y - from.y) < MIN_STEP) continue;
    points.push(from, to);
  }
  const segments = [];
  for (let index = 0; index < points.length; index += 2) {
    segments.push([points[index], points[index + 1]]);
  }
  let crossings = 0;
  for (let left = 0; left < segments.length; left += 1) {
    for (let right = left + 2; right < segments.length; right += 1) {
      if (segmentsCross(segments[left][0], segments[left][1], segments[right][0], segments[right][1])) {
        crossings += 1;
      }
    }
  }
  return crossings;
}

export function measureChunk(samples, { crossings = true } = {}) {
  if (!samples || samples.length < 2) return null;
  const first = samples[0];
  const last = samples[samples.length - 1];
  const duration = Math.max(0, last.t - first.t);
  let path = 0;
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  const steps = [];

  for (let index = 0; index < samples.length; index += 1) {
    const point = samples[index];
    minX = Math.min(minX, point.x);
    maxX = Math.max(maxX, point.x);
    minY = Math.min(minY, point.y);
    maxY = Math.max(maxY, point.y);
    if (index === 0) continue;
    const previous = samples[index - 1];
    const dx = point.x - previous.x;
    const dy = point.y - previous.y;
    const distance = Math.hypot(dx, dy);
    path += distance;
    if (distance >= MIN_STEP) steps.push({ dx, dy, dist: distance });
  }

  const turn = turningOf(steps);
  const dx = last.x - first.x;
  const dy = last.y - first.y;
  return {
    duration,
    path,
    size: Math.max(maxX - minX, maxY - minY),
    speed: duration > 0 ? (path / duration) * 1000 : 0,
    ...turn,
    crossings: crossings ? countCrossings(samples) : 0,
    dx,
    dy,
    displacement: Math.hypot(dx, dy),
  };
}

function bucketDuration(duration) {
  if (duration < 400) return 'very brief';
  if (duration < 800) return 'short';
  if (duration < 1200) return 'a moment';
  return 'sustained';
}

function bucketSize(size) {
  if (size < 30) return 'tiny';
  if (size < 80) return 'small';
  if (size < 200) return 'medium';
  if (size < 500) return 'large';
  return 'screen-filling';
}

function bucketSpeed(speed) {
  if (speed < 80) return 'slow';
  if (speed < 250) return 'steady';
  if (speed < 700) return 'fast';
  return 'very fast';
}

function bucketTurning(measurement) {
  let words = 'almost none';
  if (measurement.turningDeg >= 300) words = 'a full turn or more';
  else if (measurement.turningDeg >= 90) words = 'a lot';
  else if (measurement.turningDeg >= 20) words = 'some';
  if (Math.abs(measurement.netDeg) > 180 && Math.abs(measurement.netDeg) > measurement.turningDeg * 0.4) {
    words += measurement.netDeg > 0 ? ', mostly clockwise' : ', mostly counter-clockwise';
  }
  return words;
}

function bucketDirection(measurement) {
  if (measurement.displacement < 20 || measurement.displacement < measurement.path * 0.35) {
    return 'no single direction';
  }
  if (Math.abs(measurement.dx) > Math.abs(measurement.dy) * 1.5) {
    return measurement.dx > 0 ? 'mostly right' : 'mostly left';
  }
  if (Math.abs(measurement.dy) > Math.abs(measurement.dx) * 1.5) {
    return measurement.dy > 0 ? 'mostly down' : 'mostly up';
  }
  return 'diagonal';
}

export function describeChunk(measurement) {
  if (!measurement) return null;
  let reversals = 'many';
  if (measurement.reversals === 0) reversals = 'none';
  else if (measurement.reversals < 3) reversals = 'a few';
  let crossings = 'many';
  if (measurement.crossings === 0) crossings = 'none';
  else if (measurement.crossings < 3) crossings = 'some';
  return {
    duration: bucketDuration(measurement.duration),
    size: bucketSize(measurement.size),
    speed: bucketSpeed(measurement.speed),
    turning: bucketTurning(measurement),
    reversals,
    crossings,
    direction: bucketDirection(measurement),
  };
}

export function chunkStatus(samples) {
  if (!samples || samples.length < 2) return { close: false, reason: null };
  const duration = samples[samples.length - 1].t - samples[0].t;
  if (duration >= 1500) return { close: true, reason: 'time' };
  const measured = measureChunk(samples, { crossings: false });
  if (measured && measured.reversals >= 4) return { close: true, reason: 'reversals' };
  if (measured && measured.turningDeg >= 300) return { close: true, reason: 'turn' };
  return { close: false, reason: null };
}
